/**
 * Converts TipTap/Rich Text Editor HTML to email-safe HTML with inline styles.
 * Email clients have limited CSS support, so we need to inline all styles.
 */

interface ConversionOptions {
  baseUrl?: string;
}

// Default font stack for email compatibility
const EMAIL_FONT_FAMILY = "Arial, Helvetica, sans-serif";

// Parse inline styles from HTML
const parseInlineStyles = (styleAttr: string): Record<string, string> => {
  const styles: Record<string, string> = {};
  if (!styleAttr) return styles;
  
  styleAttr.split(';').forEach(rule => {
    const [property, value] = rule.split(':').map(s => s.trim());
    if (property && value) {
      styles[property] = value;
    }
  });
  
  return styles;
};

// Convert styles object back to string
const stylesToString = (styles: Record<string, string>): string => {
  return Object.entries(styles)
    .map(([prop, val]) => `${prop}: ${val}`)
    .join('; ');
};

// Add email-safe default styles for different elements
const getDefaultStyles = (tagName: string): Record<string, string> => {
  const defaults: Record<string, Record<string, string>> = {
    p: {
      'margin': '0 0 10px 0',
      'font-family': EMAIL_FONT_FAMILY,
      'font-size': '14px',
      'line-height': '1.5',
    },
    ul: {
      'margin': '0 0 10px 0',
      'padding-left': '20px',
      'font-family': EMAIL_FONT_FAMILY,
    },
    ol: {
      'margin': '0 0 10px 0',
      'padding-left': '20px',
      'font-family': EMAIL_FONT_FAMILY,
    },
    li: {
      'margin': '0 0 5px 0',
      'font-family': EMAIL_FONT_FAMILY,
    },
    blockquote: {
      'margin': '0 0 10px 0',
      'padding-left': '15px',
      'border-left': '3px solid #e5e5e5',
      'font-family': EMAIL_FONT_FAMILY,
      'font-style': 'italic',
    },
    a: {
      'color': '#2563eb',
      'text-decoration': 'underline',
    },
    img: {
      'max-width': '100%',
      'height': 'auto',
    },
    strong: {},
    em: {},
    u: {},
    s: {},
    span: {},
  };
  
  return defaults[tagName.toLowerCase()] || {};
};

// Process a single HTML element
const processElement = (element: Element, options: ConversionOptions): void => {
  const tagName = element.tagName.toLowerCase();
  
  // Get existing inline styles
  const existingStyle = element.getAttribute('style') || '';
  const existingStyles = parseInlineStyles(existingStyle);
  
  // Get default styles for this element type
  const defaultStyles = getDefaultStyles(tagName);
  
  // Merge styles (existing styles take precedence)
  const mergedStyles = { ...defaultStyles, ...existingStyles };
  
  // Handle images - convert relative URLs to absolute
  if (tagName === 'img' && options.baseUrl) {
    const src = element.getAttribute('src');
    if (src && !src.startsWith('http') && !src.startsWith('data:')) {
      element.setAttribute('src', `${options.baseUrl}${src.startsWith('/') ? '' : '/'}${src}`);
    }
  }
  
  // Apply merged styles
  if (Object.keys(mergedStyles).length > 0) {
    element.setAttribute('style', stylesToString(mergedStyles));
  }
  
  // Process children recursively
  Array.from(element.children).forEach(child => {
    processElement(child, options);
  });
};

/**
 * Converts TipTap HTML output to email-safe HTML with inline styles.
 * 
 * @param html - The HTML content from TipTap editor
 * @param options - Conversion options (e.g., baseUrl for image URLs)
 * @returns Email-safe HTML string with inline styles
 */
export const convertToEmailHtml = (html: string, options: ConversionOptions = {}): string => {
  if (!html || html === '<p></p>') {
    return '';
  }

  // Create a temporary container to parse the HTML
  const container = document.createElement('div');
  container.innerHTML = html;
  
  // Process all elements
  Array.from(container.children).forEach(child => {
    processElement(child, options);
  });
  
  return container.innerHTML;
};

/**
 * Wraps converted email HTML in a container div with base styles.
 * 
 * @param html - The converted email HTML
 * @returns Wrapped HTML string
 */
export const wrapEmailHtml = (html: string): string => {
  if (!html) return '';
  
  return `<div style="font-family: ${EMAIL_FONT_FAMILY}; max-width: 600px; margin: 0;">${html}</div>`;
};

/**
 * Checks if the content is plain text (no HTML tags) and converts it to basic HTML.
 * This handles legacy plain-text templates.
 * 
 * @param content - The content to check
 * @returns HTML string (either original HTML or converted from plain text)
 */
export const ensureHtml = (content: string): string => {
  if (!content) return '';
  
  // Check if content contains HTML tags
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
  
  if (hasHtmlTags) {
    return content;
  }
  
  // Convert plain text to HTML paragraphs
  return content
    .split('\n')
    .map(line => `<p style="margin: 0 0 10px 0; font-family: ${EMAIL_FONT_FAMILY};">${line || '&nbsp;'}</p>`)
    .join('');
};

/**
 * Full pipeline: ensures HTML, converts to email-safe format, and wraps.
 * 
 * @param content - The content (plain text or HTML)
 * @param options - Conversion options
 * @returns Email-ready HTML string
 */
export const prepareEmailBody = (content: string, options: ConversionOptions = {}): string => {
  const htmlContent = ensureHtml(content);
  const emailSafeHtml = convertToEmailHtml(htmlContent, options);
  return wrapEmailHtml(emailSafeHtml);
};
